import { smartBezier } from '../../../../../shared/flow-core/src/geometry'
import {
  CONDITIONAL_BOTTOM_RATIO,
  CONDITIONAL_TOP_RATIO,
  NODE_HEIGHT,
  NODE_WIDTH,
} from './constants'
import { inputPorts, outputPorts } from './ports'
import type { FlowEdge, FlowNode } from './types'

const TYPE_COLORS: Record<string, string> = {
  start: '#22c55e',
  end: '#ef4444',
  phase: '#3b82f6',
  group: '#8b5cf6',
  condition: '#f59e0b',
  action: '#06b6d4',
  'macro-start': '#a855f7',
  'macro-end': '#d946ef',
  'macro-call': '#c084fc',
}

const escapeXml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * Renders the graph as a standalone SVG document. Unlike the live canvas, ports on
 * multi-port nodes are spread evenly, matching their visual handle positions.
 */
export function buildFlowSvg(
  nodes: FlowNode[],
  edges: FlowEdge[],
  text: { typeLabel: (node: FlowNode) => string; summary: (node: FlowNode) => string },
): string {
  const pad = 80
  const minX = Math.min(...nodes.map((n) => n.x))
  const minY = Math.min(...nodes.map((n) => n.y))
  const W = Math.max(...nodes.map((n) => n.x + NODE_WIDTH)) - minX + pad * 2
  const H = Math.max(...nodes.map((n) => n.y + NODE_HEIGHT)) - minY + pad * 2
  const tx = (x: number) => x - minX + pad
  const ty = (y: number) => y - minY + pad
  const byId = new Map(nodes.map((node) => [node.id, node]))

  const inputPointExp = (node: FlowNode, port: string) => {
    const ports = inputPorts(node)
    const index = Math.max(0, ports.indexOf(port))
    const ratio =
      ports.length > 2
        ? (index + 1) / (ports.length + 1)
        : ports.length === 2
          ? index === 0
            ? 0.38
            : 0.62
          : 0.5
    return { x: tx(node.x), y: ty(node.y + NODE_HEIGHT * ratio) }
  }

  const outputPointExp = (node: FlowNode, port: string) => {
    const ports = outputPorts(node)
    const index = Math.max(0, ports.indexOf(port))
    if (node.type === 'condition') {
      const ratio = port === 'true' ? CONDITIONAL_TOP_RATIO : CONDITIONAL_BOTTOM_RATIO
      return { x: tx(node.x + NODE_WIDTH), y: ty(node.y + NODE_HEIGHT * ratio) }
    }
    if (ports.length > 1) {
      return {
        x: tx(node.x + NODE_WIDTH),
        y: ty(node.y + (NODE_HEIGHT * (index + 1)) / (ports.length + 1)),
      }
    }
    return { x: tx(node.x + NODE_WIDTH), y: ty(node.y + NODE_HEIGHT * 0.5) }
  }

  const lines: string[] = []
  lines.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`,
  )
  lines.push(`<rect width="${W}" height="${H}" fill="#f8fafc"/>`)

  // Subtle grid dots
  lines.push('<g opacity="0.25">')
  for (let gx = 0; gx <= W; gx += 20) {
    for (let gy = 0; gy <= H; gy += 20) {
      lines.push(`<circle cx="${gx}" cy="${gy}" r="1" fill="#94a3b8"/>`)
    }
  }
  lines.push('</g>')

  lines.push('<g fill="none" stroke="#94a3b8" stroke-width="2" stroke-linecap="round">')
  for (const edge of edges) {
    const from = byId.get(edge.fromNodeId)
    const to = byId.get(edge.toNodeId)
    if (!from || !to) continue
    lines.push(
      `<path d="${smartBezier(outputPointExp(from, edge.fromPort), inputPointExp(to, edge.toPort))}"/>`,
    )
  }
  lines.push('</g>')

  for (const node of nodes) {
    const nx = tx(node.x)
    const ny = ty(node.y)
    const color = TYPE_COLORS[node.type] ?? '#6b7280'
    const summary = text.summary(node)
    lines.push(
      `<rect x="${nx}" y="${ny}" width="${NODE_WIDTH}" height="${NODE_HEIGHT}" rx="8" fill="white" stroke="${color}" stroke-width="2"/>`,
    )
    lines.push(
      `<rect x="${nx}" y="${ny}" width="${NODE_WIDTH}" height="36" rx="8" fill="${color}" opacity="0.18"/>`,
    )
    lines.push(
      `<rect x="${nx}" y="${ny + 20}" width="${NODE_WIDTH}" height="16" fill="${color}" opacity="0.18"/>`,
    )
    lines.push(
      `<text x="${nx + 10}" y="${ny + 22}" font-family="system-ui,sans-serif" font-size="13" font-weight="600" fill="#0f172a">${escapeXml(node.label)}</text>`,
    )
    lines.push(
      `<text x="${nx + 10}" y="${ny + 52}" font-family="system-ui,sans-serif" font-size="11" fill="${color}">${escapeXml(text.typeLabel(node))}</text>`,
    )
    if (summary) {
      lines.push(
        `<text x="${nx + 10}" y="${ny + 70}" font-family="system-ui,sans-serif" font-size="10" fill="#94a3b8">${escapeXml(summary.substring(0, 50))}</text>`,
      )
    }
  }
  lines.push('</svg>')
  return lines.join('\n')
}
